/** @format */
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, ReactNode, useContext } from 'react';

import { MediaQuery, useMediaQuery } from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';

import { ArticleCardData } from '@toucan/client/core/contentful';

import {
    StoryCardCollection,
    StoryCardCollectionProps,
} from '@toucan/client/features/contentful/components/story-card-collection';
import { contentfulArticleCardDataToArticleCard } from '@toucan/client/features/contentful/helpers/adapters/contentful';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ArticleResultsProps
    extends Omit<StoryCardCollectionProps, 'children'> {
    /**
     * Child elements
     */
    articles?: ArticleCardData[];

    'data-id'?: string;
}

export const ArticleResults: FunctionComponent<ArticleResultsProps> = ({
    loading,
    articles = [],
    skeletonCount,
    'data-id': dataId = 'article-results',
    ...otherProps
}: ArticleResultsProps) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    const isLargeTablet = useMediaQuery(MediaQuery.LARGE_TABLET);
    const isMobile = useMediaQuery(MediaQuery.MOBILE);
    const columnCount = isMobile ? 1 : isLargeTablet ? 2 : 3; // eslint-disable-line no-nested-ternary

    /**
     * Returns an array of StoryCard components each containing a story
     */
    function renderArticles(): ReactNode {
        return articles?.map((article) =>
            contentfulArticleCardDataToArticleCard(
                article,
                activeMarket,
                t,
                dataId
            )
        );
    }

    return (
        <StoryCardCollection
            loading={loading}
            skeletonCount={skeletonCount}
            columnCount={columnCount}
            data-id={dataId}
            {...otherProps}
        >
            {renderArticles()}
        </StoryCardCollection>
    );
};
